import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, ChevronDown, ArrowRight, Sun, Moon, Sparkles, LogOut } from 'lucide-react';
import { Button } from '../ui/button';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import Logo from '../Logo';

const NAV_ITEMS = [
  {
    label: 'Services',
    href: '/services',
    submenu: [
      { label: 'ERP & CRM Agents', href: '/services/erp-crm-agents', desc: 'Automate enterprise workflows' },
      { label: 'WhatsApp Bot Flows', href: '/services/whatsapp-bots', desc: 'Move processes to WhatsApp' },
      { label: 'Dashboard & Analytics', href: '/services/dashboard-analytics', desc: 'Intelligent dashboards' },
      { label: 'Mobile App Agents', href: '/services/mobile-agents', desc: 'Smart mobile experiences' },
      { label: 'Product Development', href: '/services/product-development', desc: 'MVPs in 4-6 weeks' },
      { label: 'Website Development', href: '/services/website-development', desc: 'Modern web experiences' },
      { label: 'AI SEO Agents', href: '/services/ai-seo', desc: 'Rank higher autonomously' },
    ]
  },
  {
    label: 'AI Agents',
    href: '/ai-agents',
  },
  {
    label: 'Industries',
    href: '/industries',
  },
  { label: 'Offshore India', href: '/offshore-outsourcing' },
  { label: 'How It Works', href: '/how-it-works' },
  { label: 'Pricing', href: '/pricing' },
  { label: 'About Us', href: '/about' },
];

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const location = useLocation();
  const { isDark, toggleTheme } = useTheme();
  const { user, logout, isSuperAdmin } = useAuth();

  return (
    <>
      <header
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-2xl backdrop-saturate-150 border-b transition-colors"
        style={{ background: 'var(--nav-bg)', borderColor: 'var(--border)' }}
        data-testid="main-navbar"
      >
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2.5 group" data-testid="logo-link">
              <Logo size={36} isDark={isDark} />
              <div className="flex flex-col">
                <span className="font-heading font-semibold text-lg tracking-tight leading-none">XceedAgents</span>
                <span className="text-[10px] font-mono tracking-widest" style={{ color: 'var(--primary-accent)' }}>.AI</span>
              </div>
            </Link>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-1">
              {NAV_ITEMS.map((item) => (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => item.submenu && setActiveDropdown(item.label)}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <Link
                    to={item.href}
                    className="px-4 py-2 text-sm font-medium flex items-center gap-1 rounded-lg transition-colors hover:bg-zinc-100 dark:hover:bg-zinc-800"
                    style={{
                      color: location.pathname.startsWith(item.href)
                        ? 'var(--primary-accent)'
                        : 'var(--text-secondary)'
                    }}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {item.label}
                    {item.submenu && <ChevronDown className="w-3.5 h-3.5" />}
                  </Link>

                  <AnimatePresence>
                    {item.submenu && activeDropdown === item.label && (
                      <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 8 }}
                        transition={{ duration: 0.15 }}
                        className="absolute top-full left-0 mt-1 w-72 p-2 rounded-xl shadow-float"
                        style={{ background: 'var(--background)', border: '1px solid var(--border)' }}
                      >
                        {item.submenu.map((sub) => (
                          <Link
                            key={sub.href}
                            to={sub.href}
                            className="block px-3 py-2.5 rounded-lg transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800"
                            data-testid={`nav-sub-${sub.label.toLowerCase().replace(/\s/g, '-')}`}
                          >
                            <div className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>{sub.label}</div>
                            <div className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>{sub.desc}</div>
                          </Link>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </nav>

            {/* CTA + Theme */}
            <div className="hidden lg:flex items-center gap-3">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg transition-colors"
                style={{ color: 'var(--text-muted)', background: 'var(--input-bg)' }}
                data-testid="theme-toggle"
              >
                {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
              </button>

      {/* Hide Workspace link when not logged in - keep CTA + theme toggle only */}
              {user ? (
                <>
                  <Link to="/workspace">
                    <div
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold cursor-pointer"
                      style={{ background: 'var(--tag-bg)', color: 'var(--primary-accent)' }}
                      data-testid="nav-credits"
                      title="Available credits"
                    >
                      <Sparkles className="w-3.5 h-3.5" />
                      {user.credits ?? 0} credits
                    </div>
                  </Link>
                  <Link to={isSuperAdmin() ? "/admin" : "/workspace"}>
                    <Button variant="ghost" className="text-sm font-medium" data-testid="nav-dashboard">
                      {isSuperAdmin() ? 'Admin' : 'Workspace'}
                    </Button>
                  </Link>
                  <button
                    onClick={logout}
                    className="p-2 rounded-lg transition-colors"
                    style={{ color: 'var(--text-muted)' }}
                    data-testid="nav-logout"
                    title="Sign out"
                  >
                    <LogOut className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <Link
                  to="/login"
                  className="text-sm font-medium hover:underline transition-colors"
                  style={{ color: 'var(--text-secondary)' }}
                  data-testid="nav-login"
                  title="For existing customers"
                >
                  Sign In
                </Link>
              )}

              <Link to="/start-project">
                <Button
                  className="text-sm font-semibold px-5 hover-lift gap-1.5"
                  style={{ background: 'var(--primary)', color: 'var(--background)' }}
                  data-testid="nav-start-project"
                >
                  Request for Proposal <ArrowRight className="w-3.5 h-3.5" />
                </Button>
              </Link>
            </div>

            {/* Mobile */}
            <div className="flex items-center gap-2 lg:hidden">
              <button onClick={toggleTheme} className="p-2" style={{ color: 'var(--text-muted)' }} data-testid="theme-toggle-mobile">
                {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>
              <button className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 pt-20 px-6 lg:hidden"
            style={{ background: 'var(--background)' }}
          >
            <nav className="flex flex-col gap-1 mt-4">
              {NAV_ITEMS.map((item) => (
                <Link
                  key={item.label}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-4 text-xl font-heading font-medium border-b"
                  style={{ borderColor: 'var(--border)' }}
                >
                  {item.label}
                </Link>
              ))}
              <Link to="/start-project" onClick={() => setMobileMenuOpen(false)} className="mt-6">
                <Button className="w-full font-semibold h-12 text-base" style={{ background: 'var(--primary)', color: 'var(--background)' }}>
                  Request for Proposal
                </Button>
              </Link>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="h-16" />
    </>
  );
}
